// src/utils/videoCompression.js
//
// Client-side video compression using canvas + MediaRecorder.
// Reduces file size by 60-80% before uploading to Firebase Storage.
// Falls back to original file if compression fails or isn't supported.

const TARGET_WIDTH = 1280;  // 720p width
const TARGET_HEIGHT = 720;
const TARGET_BITRATE = 1_500_000; // 1.5 Mbps — good quality, small file
const MAX_DURATION_SECONDS = 300; // 5 min cap

/**
 * Check if the browser supports MediaRecorder with webm/vp8.
 */
export function canCompress() {
  return typeof MediaRecorder !== 'undefined' &&
    MediaRecorder.isTypeSupported('video/webm;codecs=vp8');
}

/**
 * Compress a video file using canvas re-encoding.
 *
 * @param {File} file — original video file
 * @param {(progress: number) => void} onProgress — 0–100 progress callback
 * @returns {Promise<{file: File, originalSize: number, compressedSize: number, ratio: number}>}
 */
export async function compressVideo(file, onProgress = () => {}) {
  if (!canCompress()) {
    console.warn('[compress] MediaRecorder not supported — using original file');
    return { file, originalSize: file.size, compressedSize: file.size, ratio: 1 };
  }

  const originalSize = file.size;

  // Skip compression for small files (under 10MB)
  if (originalSize < 10 * 1024 * 1024) {
    return { file, originalSize, compressedSize: originalSize, ratio: 1 };
  }

  try {
    const compressed = await reencodeVideo(file, onProgress);
    const compressedSize = compressed.size;
    const ratio = compressedSize / originalSize;

    // Only use compressed if it's actually smaller
    if (ratio < 0.9) {
      console.log(`[compress] ${(originalSize / 1024 / 1024).toFixed(1)}MB → ${(compressedSize / 1024 / 1024).toFixed(1)}MB (${Math.round(ratio * 100)}%)`);
      return {
        file: new File([compressed], file.name.replace(/\.[^/.]+$/, '.webm'), { type: 'video/webm' }),
        originalSize,
        compressedSize,
        ratio,
      };
    }

    return { file, originalSize, compressedSize: originalSize, ratio: 1 };
  } catch (err) {
    console.warn('[compress] Compression failed, using original:', err.message);
    return { file, originalSize, compressedSize: originalSize, ratio: 1 };
  }
}

/**
 * Re-encode video at lower resolution/bitrate using canvas + MediaRecorder.
 */
function reencodeVideo(file, onProgress) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    const url = URL.createObjectURL(file);
    video.src = url;

    video.onloadedmetadata = () => {
      const duration = Math.min(video.duration, MAX_DURATION_SECONDS);
      const srcW = video.videoWidth;
      const srcH = video.videoHeight;

      // Calculate target dimensions maintaining aspect ratio
      let w = srcW;
      let h = srcH;
      if (w > TARGET_WIDTH || h > TARGET_HEIGHT) {
        const scale = Math.min(TARGET_WIDTH / w, TARGET_HEIGHT / h);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }
      // Ensure even dimensions (required by some encoders)
      w = w - (w % 2);
      h = h - (h % 2);

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');

      const stream = canvas.captureStream(30); // 30fps
      const recorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp8',
        videoBitsPerSecond: TARGET_BITRATE,
      });

      const chunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        URL.revokeObjectURL(url);
        const blob = new Blob(chunks, { type: 'video/webm' });
        resolve(blob);
      };

      recorder.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(new Error('MediaRecorder error: ' + e.error?.message));
      };

      recorder.start(100); // collect data every 100ms
      video.play();

      const drawFrame = () => {
        if (video.ended || video.currentTime >= duration) {
          recorder.stop();
          video.pause();
          return;
        }

        ctx.drawImage(video, 0, 0, w, h);
        onProgress(Math.min(99, Math.round((video.currentTime / duration) * 100)));
        requestAnimationFrame(drawFrame);
      };

      video.onplay = () => requestAnimationFrame(drawFrame);

      // Safety timeout
      setTimeout(() => {
        if (recorder.state === 'recording') {
          recorder.stop();
          video.pause();
        }
      }, (duration + 5) * 1000);
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not load video for compression'));
    };
  });
}

/**
 * Get human-readable file size.
 */
export function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}
